/**
 * Lecteur .xlsx borné pour fichiers NON FIABLES (import utilisateur).
 *
 * - Format accepté : OOXML (.xlsx). Les anciens .xls binaires (OLE2) et les classeurs chiffrés
 *   sont refusés avec un message explicite.
 * - Limites : voir `XLSX_LIMITS` (taille, entrées d’archive, taux de compression, feuilles,
 *   lignes, colonnes, cellules, texte de cellule, profondeur XML).
 * - Refus : macros (vbaProject, feuilles macro), objets incorporés/ActiveX, liens externes
 *   (externalLinks, relations externes non-hyperlien), DTD/entités XML, formules dangereuses
 *   (référence externe, DDE, fonctions réseau/exécution).
 * - Les formules ne sont JAMAIS évaluées : seule la valeur mise en cache est lue.
 * - Les feuilles masquées sont ignorées (non lues, non renvoyées).
 * - La dimension déclarée de la feuille est ignorée : seules les cellules réellement présentes
 *   déterminent l’étendue (un `<dimension>` géant ne coûte rien).
 */
import { lireZipBorne } from './SafeZip';
import { XLSX_LIMITS, XlsxImportError } from './XlsxLimits';
import { FORMATS_INTEGRES, formatGeneral, formaterNombre } from './NumberFormat';
import { decoderXml, scannerXml, type XmlAttrs } from './XmlScanner';

export interface CelluleLue {
  /** Ligne (1 = première ligne). */
  ligne: number;
  /** Colonne (1 = A). */
  colonne: number;
  /** Texte affiché ; null pour une cellule d’erreur (#DIV/0!, …). */
  texte: string | null;
}

export interface FeuilleLue {
  nom: string;
  cellules: CelluleLue[];
}

export interface ClasseurLu {
  feuilles: FeuilleLue[];
}

const PARTIES_INTERDITES = [
  /(^|\/)vbaProject(Signature)?\.bin$/i,
  /^xl\/(externalLinks|embeddings|activeX|macrosheets|dialogsheets|ctrlProps)\//i,
  /^(xl\/)?(vbaData|customUI)/i,
];

const FORMULE_DANGEREUSE =
  /(\[\d+\]|\[[^\]]*\.(xls|xlsx|xlsm|xlsb)\]|https?:|ftp:|file:|\\\\|\bdde\b|\|'|!\s*['"]?[A-Za-z]:\\|\b(WEBSERVICE|IMPORTDATA|IMPORTXML|HYPERLINK|EXEC|SHELL|REGISTER|REGISTER\.ID|CALL|RTD|FILTERXML)\s*\()/i;

function octetsDe(buffer: ArrayBuffer | Uint8Array): Uint8Array {
  return buffer instanceof Uint8Array ? buffer : new Uint8Array(buffer);
}

function colonneDepuisLettres(lettres: string): number {
  let c = 0;
  for (const ch of lettres) c = c * 26 + (ch.charCodeAt(0) - 64);
  return c;
}

function decoderEchappementsXml(texte: string): string {
  return texte.indexOf('_x') === -1
    ? texte
    : texte.replace(/_x([0-9A-Fa-f]{4})_/g, (_m, h: string) => {
        const cp = parseInt(h, 16);
        return cp === 0 ? '' : String.fromCharCode(cp);
      });
}

function normaliserChemin(base: string, cible: string): string {
  if (cible.startsWith('/')) return cible.slice(1);
  const parts = base.split('/').slice(0, -1);
  for (const seg of cible.split('/')) {
    if (seg === '..') parts.pop();
    else if (seg !== '.' && seg !== '') parts.push(seg);
  }
  return parts.join('/');
}

interface Relation {
  id: string;
  type: string;
  cible: string;
  externe: boolean;
}

function lireRelations(xml: string): Relation[] {
  const out: Relation[] = [];
  scannerXml(xml, {
    open: (nom, a) => {
      if (nom === 'Relationship') {
        out.push({
          id: a['Id'] ?? '',
          type: a['Type'] ?? '',
          cible: a['Target'] ?? '',
          externe: (a['TargetMode'] ?? '').toLowerCase() === 'external',
        });
      }
    },
  });
  return out;
}

function verifierRelations(rels: Relation[]): void {
  for (const r of rels) {
    if (
      /externalLink|oleObject|vbaProject|macro|package|attachedTemplate|externalLinkPath/i.test(
        r.type,
      )
    ) {
      throw new XlsxImportError('Classeur refusé : lien externe, objet incorporé ou macro détecté');
    }
    if (r.externe && !/\/hyperlink$/i.test(r.type)) {
      throw new XlsxImportError('Classeur refusé : lien externe détecté');
    }
  }
}

function lireChainesPartagees(xml: string): string[] {
  const out: string[] = [];
  let dansSi = false;
  let dansT = false;
  let dansPhonetique = false;
  let cur = '';
  scannerXml(xml, {
    open: (nom) => {
      if (nom === 'si') {
        dansSi = true;
        cur = '';
        if (out.length >= XLSX_LIMITS.maxSharedStrings) {
          throw new XlsxImportError('Classeur refusé : trop de chaînes de caractères');
        }
      } else if (nom === 'rPh') dansPhonetique = true;
      else if (nom === 't' && dansSi && !dansPhonetique) dansT = true;
    },
    close: (nom) => {
      if (nom === 't') dansT = false;
      else if (nom === 'rPh') dansPhonetique = false;
      else if (nom === 'si') {
        dansSi = false;
        if (cur.length > XLSX_LIMITS.maxCellChars) {
          throw new XlsxImportError('Classeur refusé : texte de cellule trop long');
        }
        out.push(decoderEchappementsXml(cur));
      }
    },
    text: (t) => {
      if (dansT) cur += t;
    },
  });
  return out;
}

interface Styles {
  /** Code de format par index de style (cellXfs). */
  formats: Array<string | undefined>;
}

function lireStyles(xml: string): Styles {
  const perso = new Map<number, string>();
  const formats: Array<string | undefined> = [];
  let dansCellXfs = false;
  scannerXml(xml, {
    open: (nom, a) => {
      if (nom === 'numFmt') {
        const id = Number(a['numFmtId']);
        if (Number.isInteger(id)) perso.set(id, a['formatCode'] ?? '');
      } else if (nom === 'cellXfs') dansCellXfs = true;
      else if (nom === 'xf' && dansCellXfs) {
        if (formats.length > 100_000) throw new XlsxImportError('Classeur refusé : trop de styles');
        const id = Number(a['numFmtId'] ?? 0);
        formats.push(perso.get(id) ?? FORMATS_INTEGRES[id]);
      }
    },
    close: (nom) => {
      if (nom === 'cellXfs') dansCellXfs = false;
    },
  });
  return { formats };
}

interface InfoFeuille {
  nom: string;
  rid: string;
  masquee: boolean;
}

function lireClasseur(xml: string): { feuilles: InfoFeuille[]; date1904: boolean } {
  const feuilles: InfoFeuille[] = [];
  let date1904 = false;
  scannerXml(xml, {
    open: (nom, a) => {
      if (nom === 'workbookPr') {
        date1904 = a['date1904'] === '1' || a['date1904'] === 'true';
      } else if (nom === 'sheet') {
        if (feuilles.length >= XLSX_LIMITS.maxSheets) {
          throw new XlsxImportError(`Classeur refusé : plus de ${XLSX_LIMITS.maxSheets} feuilles`);
        }
        feuilles.push({
          nom: a['name'] ?? '',
          rid: a['id'] ?? '',
          masquee: a['state'] === 'hidden' || a['state'] === 'veryHidden',
        });
      } else if (nom === 'externalReferences' || nom === 'externalReference') {
        throw new XlsxImportError('Classeur refusé : références vers des classeurs externes');
      }
    },
  });
  return { feuilles, date1904 };
}

function serieDepuisIso(iso: string): number | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})(?:T(\d{2}):(\d{2})(?::(\d{2}))?)?/.exec(iso);
  if (!m) return null;
  const ms = Date.UTC(
    Number(m[1]),
    Number(m[2]) - 1,
    Number(m[3]),
    Number(m[4] ?? 0),
    Number(m[5] ?? 0),
    Number(m[6] ?? 0),
  );
  const jours = (ms - Date.UTC(1899, 11, 30)) / 86_400_000;
  return jours < 61 ? jours - 1 : jours;
}

function lireFeuille(
  xml: string,
  nom: string,
  chaines: string[],
  styles: Styles,
  date1904: boolean,
  compteur: { cellules: number },
): FeuilleLue {
  const cellules: CelluleLue[] = [];
  let ligneCourante = 0;
  let colonneCourante = 0;
  let cellule: { ligne: number; colonne: number; type: string; style: number } | null = null;
  let dansV = false;
  let dansT = false;
  let dansIs = false;
  let dansF = false;
  let v = '';
  let t = '';
  let formule = '';
  scannerXml(xml, {
    open: (n, a: XmlAttrs) => {
      if (n === 'row') {
        const r = Number(a['r']);
        ligneCourante = Number.isInteger(r) && r > 0 ? r : ligneCourante + 1;
        colonneCourante = 0;
        if (ligneCourante > XLSX_LIMITS.maxRows) {
          throw new XlsxImportError(
            `Classeur refusé : plus de ${XLSX_LIMITS.maxRows} lignes dans la feuille « ${nom} »`,
          );
        }
      } else if (n === 'c') {
        const ref = /^([A-Za-z]{1,3})(\d{1,7})$/.exec(a['r'] ?? '');
        if (ref) {
          colonneCourante = colonneDepuisLettres(ref[1]!.toUpperCase());
          ligneCourante = Number(ref[2]);
        } else {
          colonneCourante += 1;
        }
        if (colonneCourante > XLSX_LIMITS.maxCols) {
          throw new XlsxImportError(
            `Classeur refusé : plus de ${XLSX_LIMITS.maxCols} colonnes dans la feuille « ${nom} »`,
          );
        }
        if (ligneCourante > XLSX_LIMITS.maxRows) {
          throw new XlsxImportError(
            `Classeur refusé : plus de ${XLSX_LIMITS.maxRows} lignes dans la feuille « ${nom} »`,
          );
        }
        cellule = {
          ligne: ligneCourante,
          colonne: colonneCourante,
          type: a['t'] ?? 'n',
          style: Number(a['s'] ?? 0) || 0,
        };
        v = '';
        t = '';
        formule = '';
      } else if (n === 'v' && cellule) dansV = true;
      else if (n === 'is' && cellule) dansIs = true;
      else if (n === 't' && cellule && dansIs) dansT = true;
      else if (n === 'f' && cellule) dansF = true;
    },
    text: (txt) => {
      if (dansV) v += txt;
      else if (dansT) t += txt;
      else if (dansF) formule += txt;
      if (v.length > XLSX_LIMITS.maxCellChars * 2 || t.length > XLSX_LIMITS.maxCellChars * 2) {
        throw new XlsxImportError('Classeur refusé : texte de cellule trop long');
      }
    },
    close: (n) => {
      if (n === 'v') dansV = false;
      else if (n === 't') dansT = false;
      else if (n === 'is') dansIs = false;
      else if (n === 'f') {
        dansF = false;
        if (FORMULE_DANGEREUSE.test(formule)) {
          throw new XlsxImportError(
            'Classeur refusé : formule dangereuse (référence externe, DDE ou fonction réseau/exécution)',
          );
        }
      } else if (n === 'c' && cellule) {
        const c = cellule;
        cellule = null;
        let texte: string | null | undefined;
        switch (c.type) {
          case 's': {
            const idx = Number(v);
            texte = v === '' ? undefined : (chaines[idx] ?? '');
            break;
          }
          case 'str':
            texte = decoderEchappementsXml(v);
            break;
          case 'inlineStr':
            texte = decoderEchappementsXml(t);
            break;
          case 'b':
            texte = v === '' ? undefined : v === '1' || v === 'true' ? 'TRUE' : 'FALSE';
            break;
          case 'e':
            texte = null;
            break;
          case 'd': {
            const serie = serieDepuisIso(v);
            texte =
              serie === null
                ? v
                : formaterNombre(serie, styles.formats[c.style] ?? 'yyyy-mm-dd', date1904);
            break;
          }
          default: {
            if (v.trim() === '') {
              texte = undefined;
              break;
            }
            const x = Number(v);
            texte = Number.isFinite(x)
              ? formaterNombre(x, styles.formats[c.style], date1904)
              : formatGeneral(NaN) || v;
          }
        }
        if (texte === undefined) return;
        if (texte !== null && texte.length > XLSX_LIMITS.maxCellChars) {
          throw new XlsxImportError('Classeur refusé : texte de cellule trop long');
        }
        compteur.cellules += 1;
        if (compteur.cellules > XLSX_LIMITS.maxCells) {
          throw new XlsxImportError(`Classeur refusé : plus de ${XLSX_LIMITS.maxCells} cellules`);
        }
        cellules.push({ ligne: c.ligne, colonne: c.colonne, texte });
      }
    },
  });
  return { nom, cellules };
}

/** Lit un classeur .xlsx non fiable. Lève `XlsxImportError` (message utilisateur) si refusé. */
export function lireClasseurSecurise(buffer: ArrayBuffer | Uint8Array): ClasseurLu {
  const data = octetsDe(buffer);
  if (data.byteLength === 0) throw new XlsxImportError('Fichier Excel vide');
  if (data.byteLength > XLSX_LIMITS.maxFileBytes) {
    throw new XlsxImportError(
      `Fichier trop volumineux (maximum ${Math.round(XLSX_LIMITS.maxFileBytes / (1024 * 1024))} Mo)`,
    );
  }
  if (data[0] === 0xd0 && data[1] === 0xcf && data[2] === 0x11 && data[3] === 0xe0) {
    throw new XlsxImportError(
      'Format Excel 97-2003 (.xls) ou classeur protégé par mot de passe non pris en charge : enregistrez le fichier au format .xlsx',
    );
  }
  if (!(data[0] === 0x50 && data[1] === 0x4b)) {
    throw new XlsxImportError('Fichier non reconnu comme un classeur Excel .xlsx');
  }
  const { noms, fichiers: parties } = lireZipBorne(data, (nom) => {
    return (
      /^xl\/(workbook|sharedStrings|styles)\.xml$/.test(nom) ||
      nom === '[Content_Types].xml' ||
      nom === 'xl/_rels/workbook.xml.rels' ||
      /^xl\/worksheets\/[^/]+\.xml$/.test(nom) ||
      /^xl\/worksheets\/_rels\/[^/]+\.rels$/.test(nom)
    );
  });
  for (const nom of noms) {
    if (PARTIES_INTERDITES.some((re) => re.test(nom))) {
      throw new XlsxImportError(
        'Classeur refusé : macros, objets incorporés ou liens externes détectés',
      );
    }
  }
  const txt = (nom: string): string | undefined => {
    const p = parties.get(nom);
    return p ? decoderXml(p) : undefined;
  };
  const types = txt('[Content_Types].xml');
  if (types) {
    scannerXml(types, {
      open: (nom, a) => {
        if (nom === 'Override' && /macroEnabled|vbaProject/i.test(a['ContentType'] ?? '')) {
          throw new XlsxImportError('Classeur refusé : classeur avec macros (.xlsm)');
        }
      },
    });
  }
  const xmlClasseur = txt('xl/workbook.xml');
  if (!xmlClasseur) throw new XlsxImportError('Classeur .xlsx invalide (workbook.xml absent)');
  const { feuilles, date1904 } = lireClasseur(xmlClasseur);
  const xmlRels = txt('xl/_rels/workbook.xml.rels');
  const rels = xmlRels ? lireRelations(xmlRels) : [];
  verifierRelations(rels);
  for (const [nom, contenu] of parties) {
    if (/^xl\/worksheets\/_rels\/.+\.rels$/.test(nom))
      verifierRelations(lireRelations(decoderXml(contenu)));
  }
  const xmlChaines = txt('xl/sharedStrings.xml');
  const chaines = xmlChaines ? lireChainesPartagees(xmlChaines) : [];
  const xmlStyles = txt('xl/styles.xml');
  const styles = xmlStyles ? lireStyles(xmlStyles) : { formats: [] };
  const compteur = { cellules: 0 };
  const lues: FeuilleLue[] = [];
  for (const f of feuilles) {
    if (f.masquee) continue;
    const rel = rels.find((r) => r.id === f.rid);
    if (!rel)
      throw new XlsxImportError(`Classeur .xlsx invalide (feuille « ${f.nom} » introuvable)`);
    const chemin = normaliserChemin('xl/workbook.xml', rel.cible);
    const xml = txt(chemin);
    if (xml === undefined)
      throw new XlsxImportError(`Classeur .xlsx invalide (feuille « ${f.nom} » absente)`);
    lues.push(lireFeuille(xml, f.nom, chaines, styles, date1904, compteur));
  }
  return { feuilles: lues };
}
